import React, { useState, useEffect, useRef } from 'react';
import Analytics from 'analytics-opencare';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import PropTypes from 'prop-types';
import { Form } from 'opencare-crown';
import scrollToRef from 'utils/scrollToRef';
import sanitizeString from 'utils/sanitizeString';

// Selectors
import { updateReasonNoMatch } from 'connect/actions';

import {
  selectOngoingCareNoMatch,
  selectReasonNoMatch,
} from '../../../connect/selectors';
import {
  treatmentCheckupCleaning,
  specificConcernsTreatments,
  getTreatmentIndex,
} from '../../../../funnel/components/ConcernsTreatment/constants';

// Components
import {
  Title,
  Subtitle,
  OptionBlock,
  PrimaryButton,
  OptionTitle,
} from '../../../../common/styled-components';

import { Div } from '../../../../../common/styles/oc-styled-components';
import FloatingInput from '../../../../common/components/FloatingInput/FloatingInput';
import MultiSelect from '../../../../common/components/MultiSelect/MultiSelect';

import {
  NewPatientExamContainer,
  TreatmentsContainer,
  OtherInputContainer,
  SubmitContainer,
} from './styled-components';

const analytics = new Analytics();

const ConcernsTreatmentStep = ({ handleNextStep }) => {
  const dispatch = useDispatch();
  const refToAdditionalDetails = useRef();

  const savedAnswerOngoingCare = useSelector(selectOngoingCareNoMatch);
  const savedTreatments = useSelector(selectReasonNoMatch) || [];

  const [selectedTreatments, setSelectedTreatments] = useState(savedTreatments);

  const savedOtherTreatment = savedTreatments.find(t => t.title === 'Other');
  const [otherIsSelected, setOtherIsSelected] = useState(!!savedOtherTreatment);
  const [otherTreatment, setOtherTreatment] = useState(
    savedOtherTreatment?.note,
  );

  const selectedOngoing = savedAnswerOngoingCare === 'Yes';
  const [treatmentForm] = Form.useForm();

  useEffect(() => {
    if (otherIsSelected) {
      scrollToRef(refToAdditionalDetails);
    }
  }, [otherIsSelected]);

  // Select checkup and cleaning by default if ongoing care was selected
  useEffect(() => {
    if (selectedOngoing) {
      selectTreatment(treatmentCheckupCleaning);
    }
  }, [selectedOngoing]);

  const selectTreatment = choice => {
    const previouslySelected = selectedTreatments.find(
      treatment => treatment.id === choice.id,
    );

    if (previouslySelected) {
      const newTreatments = selectedTreatments.filter(
        treatment => treatment.id !== choice.id,
      );

      setSelectedTreatments(newTreatments);
    } else {
      setSelectedTreatments([...selectedTreatments, choice]);
    }

    if (choice.title === 'Other') {
      setOtherIsSelected(!previouslySelected);
    }
  };

  const submitSelection = () => {
    // Add freeform text if other is selected
    if (otherIsSelected) {
      selectedTreatments.find(
        treatment => treatment.id === 'other',
      ).note = sanitizeString(otherTreatment);
    }

    dispatch(updateReasonNoMatch(selectedTreatments));

    analytics.trackClick({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-account',
      name: 'form-booking',
      section: 'concerns-treatment',
      position: selectedTreatments.map(treatment =>
        getTreatmentIndex(treatment.id)),
      content: {
        response:
          selectedTreatments.map(treatment => treatment.title).join(',')
          + (otherIsSelected ? `,${sanitizeString(otherTreatment)}` : ''),
      },
    });

    handleNextStep();
  };

  const isSubmitDisabled = () =>
    !selectedTreatments.length || otherIsSelected && !otherTreatment?.length;

  return (
    <div data-testid="main-reason">
      <Title id="title">
        Do you have any specific concerns or treatments you require?
      </Title>
      <Subtitle>
        We will ensure we match you with a dentist who has expertise in these
        areas, and provide them the information.
      </Subtitle>
      <Div>
        <Form onFinish={submitSelection}>
          <OptionBlock>
            <NewPatientExamContainer>
              <MultiSelect
                key={treatmentCheckupCleaning.id}
                name="treatments"
                id={treatmentCheckupCleaning.id}
                title={treatmentCheckupCleaning.title}
                description={
                  selectedOngoing
                    ? 'All new patients joining a practice require an exam (check up) and cleaning'
                    : ''
                }
                onChange={() => selectTreatment(treatmentCheckupCleaning)}
                columns={1}
                alwaysSelected={selectedOngoing}
                initialSelected={
                  selectedTreatments.findIndex(
                    saved => saved.id === treatmentCheckupCleaning.id,
                  ) > -1 || selectedOngoing
                }
              />
            </NewPatientExamContainer>
          </OptionBlock>
          <TreatmentsContainer>
            {specificConcernsTreatments.map(treatmentCategory => (
              <div key={treatmentCategory.header}>
                <OptionTitle muted margin="25px 0 10px 10px">
                  {treatmentCategory.header}
                </OptionTitle>
                <OptionBlock>
                  {treatmentCategory.values.map(treatment => (
                    <MultiSelect
                      key={treatment.id}
                      name="treatments"
                      id={treatment.id}
                      title={`${treatment.title}${
                        treatment.title === 'Other' ? '...' : ''
                      }`}
                      tooltip={treatment.description}
                      onChange={() => selectTreatment(treatment)}
                      columns={2}
                      initialSelected={
                        selectedTreatments.findIndex(
                          saved => saved.id === treatment.id,
                        ) > -1
                      }
                    />
                  ))}
                  {/* This is a bit of a hacky way to left-align the overflowed option */}
                  {treatmentCategory.values.length % 2 !== 0 
                    && <MultiSelect columns={2} invisible />
                  }
                </OptionBlock>
              </div>
            ))}
            {otherIsSelected 
              && (
                <OtherInputContainer ref={refToAdditionalDetails} tabIndex="-1">
                  <FloatingInput
                    field="otherTreatment"
                    form={treatmentForm}
                    label="Describe other treatments"
                    initialValue={otherTreatment}
                    testId="other-additional"
                    capitalize={false}
                    onChange={e => setOtherTreatment(e.target.value)}
                  />
                </OtherInputContainer>
              )
            }
          </TreatmentsContainer>
          <SubmitContainer>
            <PrimaryButton
              type="submit"
              disabled={isSubmitDisabled()}
              data-testid="continue"
            >
              {/* If only C&C was selected */}
              {selectedTreatments.length === 1
                && selectedTreatments.includes(treatmentCheckupCleaning)
                ? 'No specific concerns or treatments needed'
                : 'Continue'}
            </PrimaryButton>
          </SubmitContainer>
        </Form>
      </Div>
    </div>
  );
};

ConcernsTreatmentStep.propTypes = {
  handleNextStep: PropTypes.func.isRequired,
};

export default ConcernsTreatmentStep;
